'use client';

import { useEngine } from '@svs/nova-poolbetting/core/frameworks/nextjs/engine';
import { DEFAULT_DRAW_ID, parseDrawId } from '@svs/nova-poolbetting/core/lib';
import { getValidatedQueryParameters } from '@svs/nova-poolbetting/core/lib/client';
import type { Definition } from '@svs/nova-poolbetting/schema/frameworks';
import type { SortDrawByType } from '@svs/nova-poolbetting/schema/input';
import { usePathname, useSearchParams } from 'next/navigation';
import { type ReactNode, useMemo } from 'react';

export type SortableDrawItem = {
  /** Stable React key (e.g. `${product}_${drawNumber}`). */
  key: string;
  /** Pre-rendered draw card (server component output). */
  node: ReactNode;
  /** Whether this draw is in TemporaryClosed status; if true, no link wrapper is rendered. */
  isTemporaryClosed: boolean;
  /** Path segment (the draw number) used when assembling the navigation href. */
  drawNumberSegment: string;
  /** Pre-computed sort keys; client picks the field based on the URL parameter. */
  sortValues: {
    regCloseTime: number;
    netSale: number;
    extraMoney: number;
  };
};

type SortableDrawListProps = {
  items: SortableDrawItem[];
  basePath: string;
};

/**
 * Computes the navigation href for a draw card, preserving any non-draw slug
 * segments in the current pathname. Mirrors the behavior of the package's
 * internal DrawSelectorCardListWrapper so initial paint keeps the same links.
 *
 * @param root0 input
 * @param root0.pathname current pathname from `usePathname()`
 * @param root0.basePath base path prefix (e.g. `''` or `/powerplay`)
 * @param root0.drawNumberSegment the draw number, as a string path segment
 * @param root0.definition the framework definition, used to validate the drawNumberSegment and
 * @returns href that targets the selected draw while keeping any sub-route slug
 */
function buildDrawHref({
  pathname,
  basePath,
  drawNumberSegment,
  definition,
}: {
  pathname: string;
  basePath: string;
  drawNumberSegment: string;
  definition: Definition;
}): string {
  const segments = pathname.split('/').filter(Boolean);
  let href = `${basePath}/${drawNumberSegment}`;

  const [, secondSegment, thirdSegment] = segments;
  if (!secondSegment) {
    return href;
  }

  let drawNumber;
  try {
    const result = parseDrawId(secondSegment, definition);
    drawNumber = result.drawNumber;
  } catch {
    drawNumber = DEFAULT_DRAW_ID;
  }

  if (drawNumber === DEFAULT_DRAW_ID) {
    href = `${href}/${secondSegment}`;
  }
  if (drawNumber !== DEFAULT_DRAW_ID && thirdSegment) {
    href = `${href}/${thirdSegment}`;
  }

  return href;
}

function compareItems(a: SortableDrawItem, b: SortableDrawItem, sortBy: SortDrawByType | undefined): number {
  switch (sortBy) {
    case 'regCloseTime':
      return a.sortValues.regCloseTime - b.sortValues.regCloseTime;
    case 'netSale':
      return b.sortValues.netSale - a.sortValues.netSale;
    case 'extraMoney':
      return b.sortValues.extraMoney - a.sortValues.extraMoney;
    default:
      return 0;
  }
}

/**
 * Renders a list of pre-rendered draw card nodes ordered by the URL's
 * `sortDrawsBy` parameter. Cards are server-rendered upstream and passed in via
 * the `items` prop; this component only reorders them on the client so Suspense
 * boundaries and data fetches are preserved when the user changes the sort.
 *
 * @param root0 component props
 * @param root0.items pre-rendered draw cards paired with their pre-computed sort keys
 * @param root0.basePath base path used when building per-card navigation links
 * @returns ordered list of draw cards wrapped in navigation links
 */
export default function SortableDrawList({ items, basePath }: SortableDrawListProps): ReactNode {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const definition = useEngine();

  const { sortDrawsBy } = useMemo(() => getValidatedQueryParameters('sortDrawsBy')(searchParams), [searchParams]);

  const orderedItems = useMemo(() => items.toSorted((a, b) => compareItems(a, b, sortDrawsBy)), [items, sortDrawsBy]);

  return (
    <ol className="divide-stroke-low divide-y [&>li:last-child_[data-slot=card]]:rounded-b-(--card-shape)">
      {orderedItems.map((item) => {
        if (item.isTemporaryClosed) {
          return <li key={item.key}>{item.node}</li>;
        }
        const href = buildDrawHref({
          pathname,
          basePath,
          drawNumberSegment: item.drawNumberSegment,
          definition: definition.model,
        });
        return (
          <li key={item.key}>
            <a href={href}>{item.node}</a>
          </li>
        );
      })}
    </ol>
  );
}
